import { ref, watch, nextTick } from 'vue';
import { Notify } from 'quasar';
import { apolloClient } from '@/apollo';
import { DOWNLOAD_FILE } from '@/components/BankStatementExport/subscriptions';
import { useFilesActions } from '@/composables/useFilesActions';
import { useUserStore, setGlobalFileDownloadSubscription } from '@/store/user';

export function useGlobalFileDownload() {
  const userStore = useUserStore();
  const { download } = useFilesActions();
  const isSubscriptionActive = ref(false);
  const subscriptionInstance = ref<{ unsubscribe(): void } | null>(null);

  const startSubscription = () => {
    console.log('startSubscription', userStore.loginData?.user?.id);
    if (!userStore.loginData?.user?.id || isSubscriptionActive.value) {
      return;
    }

    try {
      const observable = apolloClient.subscribe({
        query: DOWNLOAD_FILE,
        variables: { userId: userStore.loginData.user.id },
      });

      subscriptionInstance.value = observable.subscribe({
        next: async (response) => {
          const { fileId } = response.data?.fileDownloadReady || {};

          if (fileId) {
            try {
              await download(fileId, 'file');

              Notify.create({
                message: 'File download started successfully',
                color: 'positive',
                position: 'top',
                timeout: 3000,
              });
            } catch (error) {
              console.error('Error downloading file:', error);
              Notify.create({
                message: 'Failed to download file',
                color: 'negative',
                position: 'top',
                timeout: 5000,
              });
            }
          }
        },
        error: (error) => {
          console.error('File download subscription error:', error);
          Notify.create({
            message: 'File download subscription error',
            color: 'negative',
            position: 'top',
            timeout: 5000,
          });
          isSubscriptionActive.value = false;
        },
      });

      isSubscriptionActive.value = true;
    } catch (error) {
      console.error('Failed to start file download subscription:', error);
    }
  };

  const stopSubscription = () => {
    console.log('stopSubscription', subscriptionInstance.value);
    if (subscriptionInstance.value) {
      subscriptionInstance.value.unsubscribe();
      subscriptionInstance.value = null;
    }
    isSubscriptionActive.value = false;
  };

  // Register this composable with the user store for manual control
  setGlobalFileDownloadSubscription({
    startSubscription,
    stopSubscription,
  });

  // Start subscription immediately if user is already authenticated (e.g., after page reload)
  // Use nextTick to ensure store initialization is complete
  nextTick(() => {
    if (userStore.isAuthenticated && userStore.loginData?.user?.id) {
      startSubscription();
    }
  });

  // Watch for user data changes (in case user ID changes during session)
  watch(
    () => userStore.loginData?.user?.id,
    (userId, oldUserId) => {
      if (userId && oldUserId && userId !== oldUserId && userStore.isAuthenticated) {
        // Restart subscription with new user ID
        stopSubscription();
        startSubscription();
      }
    },
  );

  return {
    isSubscriptionActive,
    startSubscription,
    stopSubscription,
  };
}
